import React, { useState, useEffect, useCallback } from "react";
import {
    Key,
    Plus,
    Copy,
    Check,
    Trash2,
    RefreshCw,
    Eye,
    EyeOff,
    AlertCircle,
    Loader2,
    Calendar,
    Activity
} from "lucide-react";
import {
    fetchOrgApiKeys,
    createOrgApiKey,
    deleteOrgApiKey,
    rotateOrgApiKey
} from "../../api/organizations";
import { isLimitReached } from "../../utils/organizationHelpers";

const OrgApiKeysTab = ({ organization, limits, canEdit = false }) => {
    const [apiKeys, setApiKeys] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    // Create key state
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [keyName, setKeyName] = useState("");
    const [isCreating, setIsCreating] = useState(false);
    const [newKeySecret, setNewKeySecret] = useState(null);

    // Visibility state
    const [visibleKeys, setVisibleKeys] = useState({});
    const [copiedKey, setCopiedKey] = useState(null);

    // Delete state
    const [keyToDelete, setKeyToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const canCreate = canEdit && !isLimitReached(apiKeys.length, limits.apiKeys);

    const loadApiKeys = useCallback(async () => {
        if (!organization?.id) return;
        setIsLoading(true);
        setError("");
        try {
            const response = await fetchOrgApiKeys(organization.id);
            const keysData = response?.data?.data || response?.data || {};
            setApiKeys(Array.isArray(keysData.keys) ? keysData.keys : []);
        } catch (err) {
            console.error("Failed to load API keys:", err);
            setError("Unable to load API keys.");
        } finally {
            setIsLoading(false);
        }
    }, [organization?.id]);

    useEffect(() => {
        loadApiKeys();
    }, [loadApiKeys]);

    const handleCreateKey = async (e) => {
        e.preventDefault();
        if (!keyName.trim()) return;

        setIsCreating(true);
        setError("");
        try {
            const response = await createOrgApiKey(organization.id, {
                name: keyName.trim()
            });
            const newKey = response?.data?.data || response?.data || {};
            // The secret is only returned on creation
            setNewKeySecret(newKey.secret);
            setKeyName("");
            setShowCreateForm(false);
            loadApiKeys();
        } catch (err) {
            setError(err?.message || "Failed to create API key.");
        } finally {
            setIsCreating(false);
        }
    };

    const handleDeleteKey = async () => {
        if (!keyToDelete) return;
        setIsDeleting(true);
        setError("");
        try {
            await deleteOrgApiKey(organization.id, keyToDelete.id);
            setKeyToDelete(null);
            loadApiKeys();
        } catch (err) {
            setError(err?.message || "Failed to delete API key.");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleRotateKey = async (keyId) => {
        setError("");
        try {
            const response = await rotateOrgApiKey(organization.id, keyId);
            const rotatedKey = response?.data?.data || response?.data || {};
            setNewKeySecret(rotatedKey.secret);
            loadApiKeys();
        } catch (err) {
            setError(err?.message || "Failed to rotate API key.");
        }
    };

    const copyToClipboard = async (text, keyId) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedKey(keyId);
            setTimeout(() => setCopiedKey(null), 2000);
        } catch (err) {
            console.error("Failed to copy:", err);
        }
    };

    const toggleKeyVisibility = (keyId) => {
        setVisibleKeys((prev) => ({ ...prev, [keyId]: !prev[keyId] }));
    };

    const maskKey = (key) => {
        if (!key) return "••••••••••••••••";
        return key.slice(0, 12) + "••••••••••••" + key.slice(-4);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-xl font-bold dark:text-white text-slate-900">API Keys</h3>
                    <p className="text-sm dark:text-slate-400 text-slate-500">
                        {apiKeys.length} of {limits.apiKeys} API keys used
                    </p>
                </div>
                <button
                    onClick={() => setShowCreateForm(true)}
                    disabled={!canCreate}
                    className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    New API Key
                </button>
            </div>

            {/* Permission Warning */}
            {!canEdit && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-2 text-sm text-amber-400">
                    <AlertCircle className="w-4 h-4" />
                    Only organization <strong className="mx-1">admins</strong> and <strong className="mx-1">owners</strong> can manage API keys.
                </div>
            )}

            {/* Limit Warning */}
            {canEdit && isLimitReached(apiKeys.length, limits.apiKeys) && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-2 text-sm text-amber-400">
                    <AlertCircle className="w-4 h-4" />
                    API key limit reached. Upgrade your plan to create more keys.
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-sm text-red-400">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                </div>
            )}

            {/* New Key Secret Display */}
            {newKeySecret && (
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                            <Key className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-emerald-400 mb-1">
                                Save your API key now!
                            </h4>
                            <p className="text-xs text-emerald-400/70 mb-3">
                                This is the only time you will see this key. Store it securely.
                            </p>
                            <div className="flex items-center gap-2">
                                <code className="flex-1 px-3 py-2 bg-slate-900 rounded-lg text-sm font-mono text-emerald-300 overflow-x-auto">
                                    {newKeySecret}
                                </code>
                                <button
                                    onClick={() => copyToClipboard(newKeySecret, "new-key")}
                                    className="p-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors"
                                >
                                    {copiedKey === "new-key" ? (
                                        <Check className="w-4 h-4" />
                                    ) : (
                                        <Copy className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        </div>
                        <button
                            onClick={() => setNewKeySecret(null)}
                            className="text-emerald-400/50 hover:text-emerald-400"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            {/* Create Form */}
            {showCreateForm && (
                <div className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6">
                    <h4 className="text-lg font-semibold dark:text-white text-slate-900 mb-4">Create New API Key</h4>
                    <form onSubmit={handleCreateKey} className="flex gap-3">
                        <input
                            type="text"
                            value={keyName}
                            onChange={(e) => setKeyName(e.target.value)}
                            placeholder="Key name (e.g., Production API)"
                            className="flex-1 px-4 py-2 dark:bg-slate-950 bg-slate-50 border dark:border-slate-700 border-slate-300 rounded-lg text-sm dark:text-white text-slate-900 focus:ring-1 focus:ring-teal-500 outline-none"
                            required
                        />
                        <button
                            type="button"
                            onClick={() => {
                                setShowCreateForm(false);
                                setKeyName("");
                            }}
                            className="px-4 py-2 dark:bg-slate-800 bg-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700 dark:text-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isCreating || !keyName.trim()}
                            className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isCreating ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Key className="w-4 h-4" />
                            )}
                            Create Key
                        </button>
                    </form>
                </div>
            )}

            {/* API Keys List */}
            {apiKeys.length === 0 ? (
                <div className="text-center py-12 dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl">
                    <Key className="w-12 h-12 mx-auto mb-3 dark:text-slate-600 text-slate-300" />
                    <p className="dark:text-slate-400 text-slate-500">No API keys yet</p>
                    {canCreate && (
                        <button
                            onClick={() => setShowCreateForm(true)}
                            className="mt-4 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                            Create your first API key
                        </button>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    {apiKeys.map((key) => (
                        <div
                            key={key.id}
                            className="dark:bg-slate-900/50 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-5"
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-500 to-slate-700 flex items-center justify-center">
                                        <Key className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <h4 className="dark:text-white text-slate-900 font-semibold">
                                            {key.name}
                                        </h4>
                                        <div className="flex items-center gap-3 text-xs dark:text-slate-400 text-slate-500 mt-1">
                                            <span className="flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                Created {key.created_at ? new Date(key.created_at).toLocaleDateString() : "—"}
                                            </span>
                                            {key.last_used_at && (
                                                <span className="flex items-center gap-1">
                                                    <Activity className="w-3 h-3" />
                                                    Last used {new Date(key.last_used_at).toLocaleDateString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {canEdit && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleRotateKey(key.id)}
                                            className="p-2 dark:text-slate-400 text-slate-500 hover:text-teal-500 dark:hover:text-teal-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                            title="Rotate key"
                                        >
                                            <RefreshCw className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => setKeyToDelete(key)}
                                            className="p-2 dark:text-slate-400 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                            title="Delete key"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2 p-3 dark:bg-slate-950 bg-slate-100 rounded-lg">
                                <code className="flex-1 text-sm font-mono dark:text-slate-300 text-slate-600">
                                    {visibleKeys[key.id] ? key.prefix : maskKey(key.prefix)}
                                </code>
                                <button
                                    onClick={() => toggleKeyVisibility(key.id)}
                                    className="p-1.5 dark:text-slate-400 text-slate-500 hover:text-slate-300 dark:hover:text-slate-200 rounded transition-colors"
                                >
                                    {visibleKeys[key.id] ? (
                                        <EyeOff className="w-4 h-4" />
                                    ) : (
                                        <Eye className="w-4 h-4" />
                                    )}
                                </button>
                                <button
                                    onClick={() => copyToClipboard(key.prefix, key.id)}
                                    className="p-1.5 dark:text-slate-400 text-slate-500 hover:text-teal-500 dark:hover:text-teal-400 rounded transition-colors"
                                >
                                    {copiedKey === key.id ? (
                                        <Check className="w-4 h-4 text-emerald-400" />
                                    ) : (
                                        <Copy className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {keyToDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-sm shadow-2xl p-6">
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-12 h-12 bg-red-500/10 text-red-400 rounded-full flex items-center justify-center">
                                <Trash2 className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold dark:text-white text-slate-900">Delete API Key?</h3>
                                <p className="text-sm dark:text-slate-400 text-slate-500 mt-2">
                                    Are you sure you want to delete <span className="font-semibold dark:text-white text-slate-900">{keyToDelete?.name}</span>?
                                    This action cannot be undone.
                                </p>
                            </div>
                            <div className="flex gap-3 w-full mt-2">
                                <button
                                    onClick={() => setKeyToDelete(null)}
                                    className="flex-1 px-4 py-2 dark:bg-slate-800 bg-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700 dark:text-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteKey}
                                    disabled={isDeleting}
                                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isDeleting ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        "Delete"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrgApiKeysTab;
